import { Input, Button } from "@ui";
import { Form, Formik } from "formik";
import { Link } from "react-router-dom";
import style from "./LoginPage.module.scss";
import * as Yup from 'yup';
import { useSendVerifyEmailMutation, useUserLoginMutation, useUserLoginTelegramMutation } from "@RTKQuery/user/auth/auth";
import { useEffect } from "react";
import { RiLoader5Fill } from "react-icons/ri";

declare global {
    interface Window {
        Telegram?: {
            Login?: {
                auth: (options: { bot_id: string; request_access: boolean }, callback: (data: any) => void) => void;
            };
        };
    }
}

export function LoginPage() {
    const [login, { isLoading, error, reset }] = useUserLoginMutation();
    const [userLoginTelegram] = useUserLoginTelegramMutation();
    const [sendVerifyEmail, sendVerifyEmailStatus] = useSendVerifyEmailMutation();

    const script = document.createElement("script");

    function loadTelegramAPI(callback: () => void) {
        if (window.Telegram) {
            callback();
            return;
        }

        script.src = "https://telegram.org/js/telegram-widget.js?22";
        script.async = true;
        script.onload = callback;
        document.body.appendChild(script);
    }

    function authTelegram() {
        loadTelegramAPI(() => {
            if (!window.Telegram || !window.Telegram.Login) {
                console.error("Telegram API не завантажено");
                return;
            }

            window.Telegram.Login.auth(
                { bot_id: "7858055646", request_access: true },
                (data: any) => {
                    if (!data) {
                        console.error("Не отримано даних від Telegram");
                        return;
                    }
                    userLoginTelegram({
                        body: data
                    });
                }
            );
        });
    }

    useEffect(() => {
        return () => {
            try {
                document.body.removeChild(script);
            } catch { }
        }
    }, [])

    return (
        <main className={style.page}>
            <p className={style.pageName}>Увійдіть в свій обліковий запис</p>
            <Formik
                initialValues={{
                    email: "",
                    password: "",
                }}
                validationSchema={Yup.object({
                    email: Yup.string().email("Неправильний формат пошти").required("Електронна пошта - обов'язкове поле"),
                    password: Yup.string().required("Пароль - обов'язкове поле"),
                })}
                onSubmit={(values) => {
                    login(values)
                }}
            >
                {(props) => (
                    <Form>
                        <div className="flex flex-col gap-[20px] mb-[30px]">
                            <Input
                                label="Електронна пошта"
                                type="email"
                                onChange={(newValue) => {
                                    props.setFieldValue("email", newValue.trim())
                                }}
                                onBlur={props.handleBlur}
                                value={String(props.values.email)}
                                name="email"
                                customStyle={{
                                    width: "!max-w-[360px]"
                                }}
                                error={props.touched.email ? props.errors.email : ""}
                            />
                            <Input
                                label="Пароль"
                                type="password"
                                onChange={(newValue) => {
                                    props.setFieldValue("password", newValue.trim())
                                }}
                                onBlur={props.handleBlur}
                                value={String(props.values.password)}
                                name="password"
                                customStyle={{
                                    width: "!max-w-[360px]"
                                }}
                                error={props.touched.password ? props.errors.password : ""}
                            />
                        </div>
                        <Button
                            type="submit"
                            color="blue"
                            text="Увійти"
                            customStyle={{
                                width: "!max-w-full !w-full",
                                button: "!max-w-full !w-full !px-[45px] !h-[43px] !mb-[20px]",
                                text: "!text-[16px]"
                            }}
                            loading={isLoading}
                            errors={[
                                (error && ((extractError) => {
                                    const err = extractError(error)

                                    if (err === "Incorrect authentication data") {
                                        return "Невірний логін або пароль"
                                    } else if (err === "User not verify") {
                                        return {
                                            title: "От халепа!",
                                            description: <p className="max-w-[360px]">На вашу пошту надіслано лист для підтвердження. <br /> Також перевірте папку спам.</p>,
                                        button: {
                                            text: <ResendEmailButton
                                                isSuccess={sendVerifyEmailStatus.isSuccess}
                                                isLoading={sendVerifyEmailStatus.isLoading}
                                                isError={sendVerifyEmailStatus.isError}
                                                reset={sendVerifyEmailStatus.reset}
                                            />,
                                            action: () => {
                                                sendVerifyEmail({
                                                    email: props.values.email
                                                })
                                            }
                                        }
                                        }
                                    } else {
                                        return err
                                    } 
                                }))
                            ]}
                            resetStatus={() => {
                                reset();
                            }}
                        />
                        <div className="flex justify-end mb-[40px]">
                            <Link to={"../forgot-password"}>Забули пароль?</Link>
                        </div>
                        <div className="mb-[30px] flex flex-col items-center">
                            <p className={style.otherLogin}>або увійти через</p>
                            <div className="flex gap-[45px]">
                                <button type="button" onClick={authTelegram}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="41" height="40" viewBox="0 0 41 40" fill="none">
                                        <g clipPath="url(#clip0_2278_1131)">
                                            <path d="M40.8896 20.0005C40.8896 8.95479 31.9353 0.000488281 20.8896 0.000488281C9.84395 0.000488281 0.889648 8.95479 0.889648 20.0005C0.889648 31.0462 9.84395 40.0005 20.8896 40.0005C31.9353 40.0005 40.8896 31.0462 40.8896 20.0005Z" fill="#419FD9" />
                                            <path d="M40.8896 20.0005C40.8896 8.95479 31.9353 0.000488281 20.8896 0.000488281C9.84395 0.000488281 0.889648 8.95479 0.889648 20.0005C0.889648 31.0462 9.84395 40.0005 20.8896 40.0005C31.9353 40.0005 40.8896 31.0462 40.8896 20.0005Z" fill="url(#paint0_linear_2278_1131)" />
                                            <path d="M9.87915 19.5594C15.6952 16.9722 19.5735 15.2665 21.5141 14.4424C27.0546 12.0895 28.2059 11.6807 28.9563 11.6671C29.1213 11.6643 29.4903 11.706 29.7294 11.904C29.9312 12.0713 29.9867 12.2972 30.0133 12.4557C30.0399 12.6143 30.073 12.9755 30.0466 13.2576C29.7464 16.4786 28.4473 24.2952 27.7863 27.9028C27.5066 29.4293 26.956 29.9411 26.4229 29.9912C25.2643 30.1 24.3845 29.2095 23.2624 28.4584C21.5064 27.2832 20.5145 26.5515 18.81 25.4048C16.8402 24.0795 18.1171 23.351 19.2397 22.1605C19.5335 21.849 24.6382 17.1082 24.7371 16.6782C24.7494 16.6244 24.7609 16.4239 24.6442 16.318C24.5276 16.2122 24.3554 16.2484 24.2311 16.2771C24.0551 16.318 21.2499 18.211 15.8157 21.9564C15.0195 22.5146 14.2983 22.7865 13.6521 22.7723C12.9398 22.7566 11.5696 22.3611 10.5509 22.023C9.30148 21.6083 8.30843 21.3891 8.3949 20.6849C8.43994 20.318 8.93467 19.9429 9.87915 19.5594Z" fill="white" />
                                        </g>
                                        <defs>
                                            <linearGradient id="paint0_linear_2278_1131" x1="20.8896" y1="0.000488281" x2="20.8896" y2="39.7037" gradientUnits="userSpaceOnUse">
                                                <stop stop-color="#2AABEE" />
                                                <stop offset="1" stop-color="#229ED9" />
                                            </linearGradient>
                                            <clipPath id="clip0_2278_1131">
                                                <rect width="40" height="40" fill="white" transform="translate(0.889648 0.000488281)" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </button>
                                <a href="https://api.rocket-proxy.io/customer/auth/google/login">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="41" height="40" viewBox="0 0 41 40" fill="none">
                                        <path d="M37.934 20.417C37.934 19.117 37.8173 17.867 37.6007 16.667H20.334V23.767H30.2007C29.7673 26.0503 28.4673 27.9837 26.5173 29.2837V33.9003H32.4673C35.934 30.7003 37.934 26.0003 37.934 20.417Z" fill="#4285F4" />
                                        <path d="M20.3335 38.3343C25.2835 38.3343 29.4335 36.701 32.4668 33.901L26.5168 29.2843C24.8835 30.3843 22.8001 31.051 20.3335 31.051C15.5668 31.051 11.5168 27.8343 10.0668 23.501H3.9668V28.2343C6.98346 34.2176 13.1668 38.3343 20.3335 38.3343Z" fill="#34A853" />
                                        <path d="M10.0676 23.4837C9.70098 22.3837 9.48431 21.217 9.48431 20.0004C9.48431 18.7837 9.70098 17.617 10.0676 16.517V11.7837H3.96764C2.71764 14.2504 2.00098 17.0337 2.00098 20.0004C2.00098 22.967 2.71764 25.7504 3.96764 28.217L8.71764 24.517L10.0676 23.4837Z" fill="#FBBC05" />
                                        <path d="M20.3335 8.96699C23.0335 8.96699 25.4335 9.90032 27.3501 11.7003L32.6001 6.45033C29.4168 3.48366 25.2835 1.66699 20.3335 1.66699C13.1668 1.66699 6.98346 5.78366 3.9668 11.7837L10.0668 16.517C11.5168 12.1837 15.5668 8.96699 20.3335 8.96699Z" fill="#EA4335" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                        <p className={style.register}>
                            <span>Немає облікового запису?</span>
                            <Link to={"../register"}>
                                Зареєструватись
                            </Link>
                        </p>
                        <p className={style.message}>
                            Натисканням кнопки ви надаєте згоду на обробку особистих даних і погоджуєтеся з <Link to={"/privacy-policy"}>політикою конфіденційності</Link> та <Link to={"/offer"}>договором публічної оферти</Link>
                        </p>
                    </Form>
                )}
            </Formik>
        </main>
    )
}


function ResendEmailButton({ isLoading, isSuccess, isError, reset }: {
    isLoading: boolean,
    isSuccess: boolean,
    isError: boolean,
    reset: () => void
}) {
    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (isError || isSuccess) {
            timer = setTimeout(() => {
                reset()
            }, 3000)
        }

        return () => {
            clearTimeout(timer);
        }
    }, [isError, isSuccess])

    if (isLoading) {
        return (
            <p className="py-[5px]">
                <RiLoader5Fill className={"animate-spin"} />
            </p>
        )
    } else if (isSuccess) {
        return <>Успішно надіслано</>
    } else if (isError) {
        return <>Не вдалось надіслати</>
    } else {
        return <>Повторно надіслати лист</>
    }
}